'use client';

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEventHandler,
  type MouseEventHandler,
  type ReactNode,
  type RefCallback,
} from 'react';
import { createPortal } from 'react-dom';

export type EntityPreviewAnchorProps = {
  ref: RefCallback<HTMLElement>;
  'aria-describedby'?: string;
  onMouseEnter: MouseEventHandler<HTMLElement>;
  onMouseLeave: MouseEventHandler<HTMLElement>;
  onFocus: FocusEventHandler<HTMLElement>;
  onBlur: FocusEventHandler<HTMLElement>;
};

export type EntityPreviewPosition = { left: number; top: number };

type EntityPreviewTriggerProps = {
  children: (props: EntityPreviewAnchorProps) => ReactNode;
  preview: ReactNode;
  label: string;
  disabled?: boolean;
  width?: number;
  estimatedHeight?: number;
  openDelayMs?: number;
  closeDelayMs?: number;
  onOpenChange?: (open: boolean) => void;
};

type EntityPreviewCardProps = {
  eyebrow: string;
  title: string;
  metadata?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
};

const VIEWPORT_PADDING = 12;
const PREVIEW_GAP = 8;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

export function getEntityPreviewPosition(
  anchor: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>,
  preview: { width: number; height: number },
  viewport: { width: number; height: number },
): EntityPreviewPosition {
  const maxLeft = viewport.width - preview.width - VIEWPORT_PADDING;
  const maxTop = viewport.height - preview.height - VIEWPORT_PADDING;
  const fitsBelow = anchor.bottom + PREVIEW_GAP + preview.height <= viewport.height - VIEWPORT_PADDING;
  const preferredTop = fitsBelow
    ? anchor.bottom + PREVIEW_GAP
    : anchor.top - preview.height - PREVIEW_GAP;

  return {
    left: clamp(anchor.left, VIEWPORT_PADDING, maxLeft),
    top: clamp(preferredTop, VIEWPORT_PADDING, maxTop),
  };
}

export function EntityPreviewCard({
  eyebrow,
  title,
  metadata,
  description,
  children,
  className = '',
}: EntityPreviewCardProps) {
  return (
    <span className={`block p-4 ${className}`}>
      <span className="operational-eyebrow block">{eyebrow}</span>
      <span className="mt-1 block text-sm font-semibold text-content">{title}</span>
      {metadata ? <span className="mt-1 block text-xs font-medium text-content-muted">{metadata}</span> : null}
      {description ? <span className="mt-2 block text-sm leading-5 text-content-muted">{description}</span> : null}
      {children ? <span className="mt-3 block">{children}</span> : null}
    </span>
  );
}

/**
 * Adds a non-interactive quick preview to an existing link or button.
 *
 * The trigger retains click and tap ownership. The preview is supplementary,
 * opens on pointer hover or keyboard focus, and must never contain commands.
 */
export function EntityPreviewTrigger({
  children,
  preview,
  label,
  disabled = false,
  width = 320,
  estimatedHeight = 180,
  openDelayMs = 120,
  closeDelayMs = 80,
  onOpenChange,
}: EntityPreviewTriggerProps) {
  const tooltipId = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const previewRef = useRef<HTMLSpanElement | null>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const openRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<EntityPreviewPosition | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current);
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    openTimerRef.current = null;
    closeTimerRef.current = null;
  }, []);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const measured = previewRef.current?.getBoundingClientRect();
    const previewWidth = Math.min(measured?.width || width, Math.max(1, window.innerWidth - VIEWPORT_PADDING * 2));
    const previewHeight = Math.min(measured?.height || estimatedHeight, Math.max(1, window.innerHeight - VIEWPORT_PADDING * 2));
    setPosition(getEntityPreviewPosition(
      rect,
      { width: previewWidth, height: previewHeight },
      { width: window.innerWidth, height: window.innerHeight },
    ));
  }, [estimatedHeight, width]);

  const setOpenState = useCallback((nextOpen: boolean) => {
    if (openRef.current === nextOpen) return;
    openRef.current = nextOpen;
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [onOpenChange]);

  const openNow = useCallback(() => {
    if (disabled) return;
    clearTimers();
    updatePosition();
    setOpenState(true);
  }, [clearTimers, disabled, setOpenState, updatePosition]);

  const openAfterDelay = useCallback(() => {
    if (disabled) return;
    clearTimers();
    openTimerRef.current = window.setTimeout(openNow, openDelayMs);
  }, [clearTimers, disabled, openDelayMs, openNow]);

  const closeAfterDelay = useCallback(() => {
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => setOpenState(false), closeDelayMs);
  }, [clearTimers, closeDelayMs, setOpenState]);

  const closeNow = useCallback(() => {
    clearTimers();
    setOpenState(false);
  }, [clearTimers, setOpenState]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, preview, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNow();
    };
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closeNow, open, updatePosition]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const anchorProps: EntityPreviewAnchorProps = {
    ref: (node) => { anchorRef.current = node; },
    'aria-describedby': open ? tooltipId : undefined,
    onMouseEnter: openAfterDelay,
    onMouseLeave: closeAfterDelay,
    onFocus: openNow,
    onBlur: closeNow,
  };

  return (
    <>
      {children(anchorProps)}
      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <span
              ref={previewRef}
              id={tooltipId}
              role="tooltip"
              aria-label={label}
              className="ss-overlay-panel pointer-events-none fixed z-overlay block max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-card border text-left text-content shadow-e3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-reduce:animate-none"
              data-ss-overlay-material="transient"
              style={{ left: position.left, top: position.top, width: `min(${width}px, calc(100vw - 1.5rem))` }}
            >
              {preview}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
